import type { SupportedLanguage } from "@/services/locale";
import { i18n } from "@/services/locale";
import { useAiNotificationStore } from "@/stores/ai-notification";
import { useDataProcessingStore } from "@/stores/data-processing";
import { useWorkspaceStore } from "@/stores/workspace";
import type { AiAssistant } from "@/types/ai/Assistant";
import { Assistant } from "./Assistant";

/**
 * Workspace Assistant - Helps users accomplish tasks in the workspace
 */
export class WorkspaceAssistant extends Assistant {
  private dataflowId: string | undefined;
  private language: SupportedLanguage;

  constructor(dataflowId: string | undefined) {
    super();
    this.dataflowId = dataflowId;
    this.language = i18n.getCurrentLanguage();
  }

  config(): AiAssistant {
    return {
      systemPrompt: this.getSystemPrompt(),
      model: "gpt-4o-mini",
      temperature: 0.3,
      maxTokens: 500,
      language: this.language,
      responseFormat: "json_object",
    };
  }

  private getActionsSchema(): Record<string, any> {
    return {
      map: {
        fly: {
          data: {
            center: "[lng, lat] (number array)",
            zoom: "number? (3-16)",
            location: "string? (place name for highlighting)",
          },
          note: "Use 'center' [lng, lat].",
        },
        toggleLayer: {
          data: {
            layerId: "municipalities|countries|timezones",
            visible: "boolean",
          },
        },
      },
      workspace: {
        setView: { data: { view: "map|table|json|chart|flow|diagram|make" } },
        openSidebar: { data: { mode: "discover|flow|layers|null" } },
        closeSidebar: { data: {} },
        openModal: {
          data: {
            mode: "data|project|geocode|enhancement|paste|group-config|null",
            title: "string?",
            modalData: "object?",
          },
        },
        closeModal: { data: {} },
      },
      dataflow: {
        addTransformer: {
          data: {
            transformer: {
              type: "select|calculate|where|where_sql|sort|column_type|column_name|fill_missing|find_replace|upper_lower|join|group|data_output",
              item_list: "array",
            },
          },
          note: 'For \'where\' type: item_list format is [{column__operator: value}]. CRITICAL: The column name is REQUIRED and must come from COLUMNS list. Format: {"EXACT_COLUMN_NAME__eq": "value"}. Operators: eq (equals), ne (not equals), gt (greater than), gte (>=), lt (<), lte (<=), contains, icontains. Example: [{"postal_code__eq": "2500"}] filters to rows where postal_code equals 2500. The key MUST be "column_name__eq" where column_name is from COLUMNS list. NEVER omit the column name! Never include data_input/user_file.',
        },
        updateTransformers: {
          data: { transformers: "array" },
          note: "Replaces all transformers",
        },
        updateMetadata: {
          data: {
            dataflow_name: "string?",
            dataflow_description: "string?",
          },
        },
      },
      category: {
        setColumn: {
          data: { columnName: "string|null" },
          note: "Match column name from COLUMNS list using fuzzy matching rules. Use null to deselect/hide all groups.",
        },
        setConfig: {
          data: {
            category: "string",
            config: { name: "string?", color: "string?" },
          },
        },
        setVisible: {
          data: {
            categories: "string[]|[]|null",
          },
        },
      },
    };
  }

  getSystemPrompt(): string {
    // Read columns and metadata directly from data processing store
    const dataProcessingStore = useDataProcessingStore.getState();
    const processedData = this.dataflowId
      ? dataProcessingStore.processedData[this.dataflowId]
      : undefined;
    const processedDataMetadata = processedData?.metadata;

    // Get columns from processed data metadata or from first row
    const availableColumns =
      processedDataMetadata?.columns ||
      (processedData?.data && processedData.data[0]
        ? Object.keys(processedData.data[0])
        : []);

    const actionsSchema = this.getActionsSchema();
    const columnsList =
      availableColumns.length > 0
        ? `\n═══════════════════════════════════════════════════════════════
AVAILABLE COLUMNS (USE EXACT NAMES FROM THIS LIST):
${availableColumns.map((col, i) => `  ${i + 1}. "${col}"`).join("\n")}
═══════════════════════════════════════════════════════════════\n`
        : "\n⚠️ WARNING: No columns available.\n";

    return `
      You are an AI assistant for Atlasio workspace. Return JSON only: {"reply": "message", "actions": [{"context": "...", "action": "...", "data": {...}}]}
      
      ${columnsList}
      
      ACTIONS: ${JSON.stringify(actionsSchema, null, 2)}
      
      COLUMN MATCHING (CRITICAL - READ CAREFULLY):
      STEP 1: When user mentions a column (e.g., "postal code", "city", "postnummer"), identify what column they're referring to
      STEP 2: Look at the COLUMNS list above and find the EXACT column name that matches
      STEP 3: Use this matching process:
        a) Normalize the user's term: lowercase, remove spaces/underscores/special chars
        b) For each column in COLUMNS list, normalize it the same way
        c) Check for:
           - Exact match (after normalization)
           - Contains match (user term contains column name or vice versa)
           - Semantic match (postal code → postcode, zip, postal_code, zip_code, postnummer)
        d) Choose the BEST matching column from COLUMNS list
      STEP 4: Use the EXACT column name from COLUMNS list (case-sensitive, with exact spelling)
      
      MATCHING EXAMPLES:
      - User says "postal code" → Look for columns like: "postal_code", "postcode", "zip_code", "postnummer", etc.
      - User says "postnummer" (Danish) → Match to "postal_code" or "postcode" if available
      - User says "city" → Match to "city", "municipality", "town", "kommune", "stad", etc.
      - User says "by" (Danish for city) → Match to "city", "municipality", etc.
      - User says "zip" → Match to "postal_code", "zip_code", "postcode", etc.
      - User says "amount" → Match to "amount", "total_amount", "sum", etc.
      
      COMMON MULTILINGUAL TERMS:
      - Postal codes: "postal code", "postcode", "zip code", "zipcode", "postnummer" (DA/SV), "postal_code", "zip_code"
      - City/Location: "city", "town", "municipality", "kommune" (DA), "stad" (SV), "by" (DA)
      - Country: "country", "land" (DE/NL), "pays" (FR), "país" (ES)
      - Name: "name", "navn" (DA/NO), "nombre" (ES), "nom" (FR)
      
      CRITICAL RULES:
      - ALWAYS use the EXACT column name from the COLUMNS list above
      - NEVER invent or create new column names
      - If you can't find a match, look more carefully at the COLUMNS list
      - The column name must match EXACTLY (including underscores, case, etc.)
      - If multiple columns could match, choose the most semantically appropriate one
      - BEFORE using a column name in your response, VERIFY it exists in the COLUMNS list above
      - If the column doesn't exist in the list, you MUST find the closest match or return an error
      
      FILTERING REQUESTS:
      - When user asks to "show only X", "filter to X", "show X on the map", or similar filtering requests, use dataflow.addTransformer with type "where"
      - CRITICAL: The transformer MUST include BOTH the column name AND the value
      - Format: {"column_name__eq": "value"} where:
        * column_name is the EXACT name from COLUMNS list (REQUIRED - never omit this!)
        * __eq is the operator (for exact matches)
        * "value" is the filter value
      
      STEP-BY-STEP PROCESS FOR FILTERING:
      STEP 1: Parse user request to identify:
        a) The COLUMN they're referring to (e.g., "postnummer", "postal code", "city")
        b) The VALUE to filter by (e.g., "2500", "Copenhagen")
      STEP 2: Match the column term to an EXACT column name from COLUMNS list above
        - "postnummer" (Danish) → find "postal_code", "postcode", "zip_code", etc. in COLUMNS
        - "postal code" → find "postal_code", "postcode", etc. in COLUMNS
        - "city" → find "city", "municipality", "town", etc. in COLUMNS
      STEP 3: Create the filter object: {"EXACT_COLUMN_NAME__eq": "value"}
        - The KEY of the object MUST be: "column_name__eq" (column name + double underscore + operator)
        - Use the EXACT column name from COLUMNS list (case-sensitive, exact spelling)
        - NEVER omit the column name - it's REQUIRED!
        - The format is ALWAYS: {"column_from_COLUMNS_list__eq": "value"}
        - Example: If COLUMNS has "postal_code" and user says "Postnummer 2500", use {"postal_code__eq": "2500"}
        - NOT {"__eq": "2500"} (missing column!)
        - NOT {"Postnummer__eq": "2500"} (wrong column name!)
      STEP 4: Wrap in transformer action:
        {"context": "dataflow", "action": "addTransformer", "data": {"transformer": {"type": "where", "item_list": [{"EXACT_COLUMN_NAME__eq": "value"}]}}}
      
      FILTERING EXAMPLES (MUST include column name):
      * User: "Show only postal code 2500"
        → Column: "postal code" → Match to "postal_code" in COLUMNS
        → Value: "2500"
        → Action: {"context": "dataflow", "action": "addTransformer", "data": {"transformer": {"type": "where", "item_list": [{"postal_code__eq": "2500"}]}}}
        ✓ Note: "postal_code" is the EXACT column name from COLUMNS list
      
      * User: "Postnummer 2500" or "postnummer 2500" (Danish)
        → Column: "Postnummer"/"postnummer" means "postal code" → Look in COLUMNS for: "postal_code", "postcode", "zip_code", etc.
        → Value: "2500"
        → CORRECT: {"postal_code__eq": "2500"} (column name + __eq + value)
        → WRONG: {"__eq": "2500"} (missing column name!)
        → WRONG: {"Postnummer__eq": "2500"} (using user's term instead of COLUMNS list name!)
        → Action: {"context": "dataflow", "action": "addTransformer", "data": {"transformer": {"type": "where", "item_list": [{"postal_code__eq": "2500"}]}}}
        ✓ The key MUST be "column_name__eq" where column_name comes from COLUMNS list
      
      * User: "Filter to city Copenhagen"
        → Column: "city" → Match to "city" or "municipality" in COLUMNS
        → Value: "Copenhagen"
        → Action: {"context": "dataflow", "action": "addTransformer", "data": {"transformer": {"type": "where", "item_list": [{"city__eq": "Copenhagen"}]}}}
        ✓ Note: "city" is the EXACT column name from COLUMNS list
      
      VALIDATION CHECKLIST (before returning response):
      ✓ Did I identify the column the user is referring to?
      ✓ Did I find the EXACT matching column name in the COLUMNS list above?
      ✓ Is the column name included in the filter object? (e.g., {"column_name__eq": "value"})
      ✓ Is the column name spelled EXACTLY as it appears in COLUMNS list?
      ✓ Is the value included?
      ✓ Is the operator correct (__eq for exact matches)?
      
      REMEMBER: The column name is MANDATORY - never create a filter without it!
      
      REPLY GUIDELINES (user sees this after action completes):
      - Make it short, fun, and relevant to what was just done
      - Use past tense or present perfect (action completed)
      - Be conversational and friendly
      - Keep it under 15 words
      - Examples:
        * "Show me Spain" → reply: "Found Spain!" or "There's Spain on the map!"
        * "Filter by city = Copenhagen" → reply: "Filtered to Copenhagen" or "Showing only Copenhagen now"
        * "Show only postal code 2500" → reply: "Filtered to postal code 2500!" or "Showing only 2500 now"
        * "Group by country" → reply: "Grouped by country!" or "Data is now grouped by country"
        * "Show Denmark" → reply: "Denmark is on the map!" or "Zooming to Denmark"
        * "Add a filter" → reply: "Filter added!" or "Done! Your filter is active"
    `;
  }

  async execute(prompt: string): Promise<void> {
    const { showNotification } = useAiNotificationStore.getState();
    const { execute } = useWorkspaceStore.getState();
    const response = await Assistant.sendPrompt<{ actions?: unknown[] }>(
      prompt,
      this.config()
    );

    if (response.error) {
      showNotification(response.error, true);
      return;
    }

    if (response.reply) {
      showNotification(response.reply, false);
    }

    if (response.actions && Array.isArray(response.actions)) {
      await execute(this.dataflowId, response.actions as any);
    }
  }
}
